import Phaser from 'phaser';

import { asyncLoad, makeStandardController, StandardController } from 'tower/util';
import BaseScene from './base';

import gameState, { sharedState } from 'tower/state/game-state';
import { SharedStateModification, WorkerMessage, WorkerReceiverMessage } from 'tower/types';
import * as C from 'tower/constants';
import worker from 'tower/state/worker';

export default class LoadingScene extends BaseScene {
  constructor() {
    super({
      key: 'loading',
    });
  }

  ready = false;
  controller!: StandardController;

  preload() {
    // Load resources necessary for loading screen only
    this.load.bitmapFont('textWhite', 'fonts/text_white.png', 'fonts/text.fnt');
    this.load.bitmapFont('textRed', 'fonts/text_red.png', 'fonts/text.fnt');
    this.load.bitmapFont('textBlue', 'fonts/text_blue.png', 'fonts/text.fnt');
    this.load.bitmapFont('textGreen', 'fonts/text_green.png', 'fonts/text.fnt');
    this.load.spritesheet('fairy', 'img/fairy.png', { frameWidth: 8, frameHeight: 16 });
    this.load.image('box', 'img/box.png');
  }

  create() {
    super.create();

    this.anims.create({
      key: `fairy`,
      frameRate: 5,
      frames: this.anims.generateFrameNumbers('fairy', {
        frames: [0, 1],
      }),
      repeat: -1,
    });

    this.controller = makeStandardController(this);

    const loadingText = this.add.bitmapText(88, 104, 'textWhite', 'LOADING...');

    const keyboardBox = this.add.nineslice(64, 212, 'box', 0, 112, 72, 8, 8, 8, 8);
    const keyboardCutout = this.add.rectangle(64, 180, 80, 8, 0x000000);
    const keyboardTitle = this.add.bitmapText(32, 176, 'textWhite', 'KEYBOARD');
    const keyboardText = this.add.bitmapText(24, 192, 'textWhite', 'ARROW KEYS\n\n   D & F\n\n  RETURN');

    const gamepadBox = this.add.nineslice(196, 212, 'box', 0, 104, 72, 8, 8, 8, 8);
    const gamepadCutout = this.add.rectangle(196, 180, 72, 8, 0x000000);
    const gamepadTitle = this.add.bitmapText(168, 176, 'textWhite', 'GAMEPAD');
    const gamepadText = this.add.bitmapText(176, 192, 'textWhite', 'D-PAD\n\nA & B\n\nSTART');

    const fairy = this.add.sprite(0, 0, 'fairy', 0).play('fairy');
    const path = new Phaser.Curves.Path();
    path.add(new Phaser.Curves.Ellipse(104, 72, 24, 16, 0, 360, true));
    path.add(new Phaser.Curves.Ellipse(152, 72, 24, 16, 0, 360, false, 180));

    const tween = this.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 4000,
      loop: -1,
      onUpdate(tween) {
        const value = tween.getValue()!;
        const point = path.getPoint(value);
        fairy.setPosition(point.x, point.y);
      },
    });

    const rejectedText = this.add
      .bitmapText(36, 104, 'textWhite', '  THIS GAME CANNOT BE\n\n  PLAYED IN MORE THAN\n\nTWO TABS SIMULTANEOUSLY')
      .setVisible(false);

    (async () => {
      try {
        await Promise.all([
          asyncLoad(this, () => {
            for (const scene of this.game.scene.scenes) {
              (scene as BaseScene).loadResources?.(this);
            }
          }),
          this.maybeInitState(),
        ]);
      } catch (err) {
        fairy.setVisible(false);
        loadingText.setVisible(false);

        keyboardBox.setVisible(false);
        keyboardCutout.setVisible(false);
        keyboardTitle.setVisible(false);
        keyboardText.setVisible(false);

        gamepadBox.setVisible(false);
        gamepadCutout.setVisible(false);
        gamepadTitle.setVisible(false);
        gamepadText.setVisible(false);

        rejectedText.setVisible(true);
        return;
      }

      loadingText.setText('PRESS  ANY\n\n TO START');
      tween.completeAfterLoop().on('complete', () => {
        this.tweens.add({
          targets: [fairy],
          duration: 1500,
          y: -32,
        });
      });

      // Display touch controls for devices with touchscreens as the primary control
      if (window.matchMedia('(pointer: coarse)').matches) {
        document.querySelector('#touch-controls')?.classList.remove('hidden');
      }
      this.ready = true;
    })();
  }

  handleButtonPress = async () => {
    this.ready = false;

    await this.fadeOut();

    // Start the show
    this.scene.start(gameState.overridden ? 'level-intro' : 'title');
  };

  update() {
    const { controller, ready } = this;
    if (ready) {
      if (Object.values(controller).some((b) => b.isDown)) {
        this.handleButtonPress();
      }
    }
  }

  async maybeInitState() {
    if (gameState.stateInitComplete) {
      return;
    }

    // If an override is present, apply it
    let overridden = false;
    for (const key of Object.keys(C.OVERRIDES)) {
      if (window.location.search.includes(key)) {
        gameState.applyOverride(C.OVERRIDES[key]);
        gameState.deathReset();
        overridden = true;
        break;
      }
    }

    // Wrap hooks in a promise that resolves once the first sync is received so we can block.
    return new Promise<void>((resolve, reject) => {
      // Set up repeated ping to let worker know we're alive
      const pingId = setInterval(() => {
        worker.port.postMessage({ type: 'ping' });
      }, 1000);

      // If we're closing, attempt to let the worker know
      window.addEventListener('unload', () => {
        worker.port.postMessage({ type: 'disconnect' });
      });

      worker.port.addEventListener('message', (event: MessageEvent<WorkerReceiverMessage>) => {
        const msg = event.data;
        switch (msg.type) {
          case 'ping': {
            console.log('pong');
            break;
          }
          case 'ackUnsynced':
            resolve();
            return;

          case 'rejectConnection':
            gameState.stateInitComplete = true;
            clearInterval(pingId);
            reject(new Error('rejected'));
            break;
        }
      });
      worker.port.start();

      // Clear worker storage if resetting state
      if (window.location.search.includes('reset')) {
        const msg: WorkerMessage = {
          type: 'clearStorage',
        };
        worker.port.postMessage(msg);
      }

      // If we applied an override, request a session without synced state
      if (overridden) {
        const msg: WorkerMessage = {
          type: 'synUnsynced',
        };
        worker.port.postMessage(msg);
        return;
      }

      // Apply changes from the worker to our local shared state
      worker.port.addEventListener('message', (event: MessageEvent<WorkerReceiverMessage>) => {
        const msg = event.data;
        switch (msg.type) {
          case 'applySharedStateModification':
            sharedState.applyModification(msg.mod, false);
            break;
          case 'syncSharedState':
            sharedState.applySerialized(msg.serializedSharedState);
            gameState.stateInitComplete = true;
            gameState.deathReset();
            resolve();
            break;
        }
      });

      // Forward changes to our local shared state back to the worker
      sharedState.on('applymodification', (mod: SharedStateModification) => {
        const msg: WorkerMessage = {
          type: 'applySharedStateModification',
          mod,
        };
        worker.port.postMessage(msg);
      });

      // Request initial sync
      const msg: WorkerMessage = {
        type: 'getSharedState',
      };
      worker.port.postMessage(msg);
    });
  }
}
